/**
 * Что будет после ближайшего прихода: придёт, уйдёт, останется.
 *
 * Главная отвечала на вопрос «сколько до прихода» и молчала о том, что
 * дальше. У владельца 9 октября кредитка на 151 975 ₽, а 5–12 октября — ещё
 * пять платежей; сколько останется от зарплаты, приходилось складывать в
 * голове из четырёх разных блоков. Первый внешний пользователь попросил
 * ровно это: «две чаши весов — весь доход и весь расход».
 *
 * Считается отрезок в месяц от даты ближайшего прихода — по событиям общего
 * прогноза (`forecast.ts`), а не своим календарём. Внутри:
 *
 * · **придёт** — поступления по ритму дохода, **осторожной оценкой**, как и
 *   в проверке покупки. Прежде здесь стояла названная сумма с оговоркой, и
 *   один и тот же приход на соседних экранах стоил по-разному: у владельца
 *   290 000 ₽ здесь и 261 000 ₽ в прогнозе. Оговорка у плавающего дохода
 *   осталась — теперь она говорит, что сумма взята по нижней оценке;
 * · **платежи** — неоплаченное, что наступит в отрезке, **за вычетом уже
 *   отложенного**: под отложенное сегодняшние деньги уже заперты, и приход на
 *   них не нужен. Кредитки — оба платежа: остаток выписки и покупки после
 *   неё, если их срок попал в отрезок;
 * · **жизнь** — обычные траты по дням отрезка, как в прогнозе: месяц в 31
 *   день стоит на день дороже месяца в 30.
 *
 * Это прогноз, а не деньги: сюда не входит ничего из того, что уже на
 * счетах, и «Не закреплено» от него не меняется.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { addDays, addMonths, isAfter, isBefore, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { forecast, notCovered } from './forecast.js';
import type { IncomeFact } from './income.js';
import type {
  Account, CreditTerms, IncomeStream, Obligation, Reserve,
} from './types.js';

export interface OutlookLine {
  label: string;
  date: PlainDate;
  amount: Money;
}

export interface IncomeOutlook {
  /** Первый день отрезка — дата ближайшего прихода. */
  from: PlainDate;
  /** Последний день отрезка. */
  to: PlainDate;
  income: Money;
  incomeLines: OutlookLine[];
  /** Есть ли среди прихода плавающие суммы: они взяты по нижней оценке. */
  variable: boolean;
  payments: Money;
  /** Платежи по убыванию суммы: крупные важнее для решения. */
  paymentLines: OutlookLine[];
  /** Обычная жизнь за отрезок; `null` — человек её не назвал. */
  living: Money | null;
  /** Что останется; `null`, пока жизнь неизвестна: иначе остаток завышен. */
  left: Money | null;
}

export function incomeOutlook(input: {
  today: PlainDate;
  /** Дата ближайшего прихода. Без неё отрезка нет. */
  horizon: PlainDate | null;
  accounts: readonly Account[];
  creditTerms: readonly CreditTerms[];
  incomeStreams: readonly IncomeStream[];
  obligations: readonly Obligation[];
  /** Конверты как записаны: прогноз сам урежет их до денег на своём счёте. */
  reserves: readonly Reserve[];
  needBaseline: Amount;
  wantBaseline: Amount;
  /** С какого дня приложение ведёт учёт: раньше платежи не считаются долгом. */
  trackingStartedAt?: PlainDate;
  /** Приходы, которые случились: пришедшее раньше срока второй раз не ждём. */
  incomeFacts?: readonly IncomeFact[];
}): IncomeOutlook | null {
  const from = input.horizon;
  if (!from || !isAfter(from, input.today)) return null;
  const to = addDays(addMonths(from, 1), -1);

  /*
    Конверты расходуются по порядку сроков — с начала учёта, а не с даты
    прихода: так их раскладывает сам прогноз. Иначе конверт, отложенный под
    сегодняшний кредит авто, «покрыл» бы октябрьский, и из прихода вычлось бы
    на его сумму меньше правды.
  */
  const f = forecast({
    today: input.today,
    to,
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    reserves: input.reserves,
    needBaseline: isKnown(input.needBaseline) ? input.needBaseline.value : ZERO,
    wantBaseline: isKnown(input.wantBaseline) ? input.wantBaseline.value : ZERO,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
    ...(input.incomeFacts ? { incomeFacts: input.incomeFacts } : {}),
  });
  /*
    Отрезок кончается накануне следующего прихода того же дохода, а не ровно
    через месяц (02.11.2026). ROWI 5-го с переносом на рабочий день: 5 декабря
    2026 — суббота, приход 4-го, и при «месяц минус день» он попадал внутрь
    отрезка «5 ноября – 4 декабря» вторым — доход месяца считался дважды.
  */
  const openers = new Set(f.events.filter((e) => e.kind === 'income' && e.date === from).map((e) => e.ref));
  const again = f.events
    .filter((e) => e.kind === 'income' && openers.has(e.ref) && isAfter(e.date, from) && !isAfter(e.date, to))
    .map((e) => e.date).sort()[0];
  const end = again ? addDays(again, -1) : to;
  const inside = f.events.filter((e) => !isBefore(e.date, from) && !isAfter(e.date, end));

  const incomeLines: OutlookLine[] = inside
    .filter((e) => e.kind === 'income')
    .map((e) => ({ label: e.label, date: e.date, amount: e.amount }));
  const variable = inside.some((e) => e.kind === 'income'
    && input.incomeStreams.some((s) => s.id === e.ref && s.isVariable));

  const paymentLines: OutlookLine[] = inside
    .filter((e) => e.kind === 'payment' || e.kind === 'card')
    .map((e) => ({ label: e.label, date: e.date, amount: notCovered(e) }))
    .filter((l) => l.amount > ZERO)
    .sort((a, b) => b.amount - a.amount);

  const income = add(...incomeLines.map((l) => l.amount));
  const payments = add(...paymentLines.map((l) => l.amount));
  const living = isKnown(input.needBaseline)
    ? add(...inside.filter((e) => e.kind === 'living').map((e) => e.amount))
    : null;
  const left = living === null ? null : sub(sub(income, payments), living);

  return {
    from, to: end, income, incomeLines, variable, payments, paymentLines, living, left,
  };
}

/** Остаток без знака минус — для строки «не хватит N». */
export const outlookGap = (o: IncomeOutlook): Money =>
  (o.left !== null && o.left < ZERO ? clampPos(sub(ZERO, o.left)) : ZERO);
